import { asAccountId } from "@repo/app-types/entities/account";
import { getCompanyTimezone } from "@repo/app-types/entities/company";
import {
  asPlaybookId,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import {
  asTeammateId,
  buildTeammateDisplayNames,
  type TeammateId,
} from "@repo/app-types/entities/teammate";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import {
  CustomerTimeline,
  CustomerTimelineSkeleton,
} from "@repo/ui/customerTimeline/customerTimeline";
import { FilterBar } from "@repo/ui/Filters/filterBar";
import { Size } from "@repo/ui/helpers/helpers";
import { useInfiniteScroll } from "@repo/ui/helpers/useInfiniteScroll";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
  type RefObject,
} from "react";
import { useParams } from "react-router";
import { twMerge } from "tailwind-merge";
import {
  useCustomerDetailQuery,
  useCustomerTimelineInfiniteQuery,
} from "../../api/entities/customer.js";
import { appPaths } from "../../app/appPaths.js";
import { requireRouteParam } from "../../app/routeParams.js";
import { PageBody, PageLayout } from "../../components/layout/pageLayout.js";
import { useAppSelector } from "../../hooks/hook.js";
import {
  NavigationSlideoverContainer,
  useModalNavigation,
} from "../../hooks/useModalNavigation.js";
import { PH_MASK_CLASS } from "../analytics/sessionRecordingMasking.js";
import { selectCompany } from "../company/companySlice.js";
import { CustomerInfoPopover } from "./customerInfoPopover.js";
import { CustomerPlaybookScores } from "./customerPlaybookScores.js";
import {
  buildCustomerTimelineRow,
  buildCustomerTimelineRowId,
} from "./timeline/customerTimelineRow.js";
import type { OpenCustomerInteraction } from "./timeline/timelineRowHelpers.js";
import {
  buildUpcomingActionRow,
  buildUpcomingActionRowId,
} from "./timeline/upcomingActionRow.js";

const SCORE_CARD_ENTRY_DURATION_MS = 300;
const SCORE_CARD_START_DELAY_MS = 300;

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

interface ScoreCardEntryProps {
  timelineRef: RefObject<HTMLDivElement | null>;
  children: ReactNode;
}

const ScoreCardEntry: FC<ScoreCardEntryProps> = (props) => {
  const { timelineRef, children } = props;
  const [isInteractive, setIsInteractive] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const card = cardRef.current;
    const timeline = timelineRef.current;
    if (!card || !timeline) {
      return;
    }
    if (prefersReducedMotion() || !timeline.animate) {
      setIsInteractive(true);
      return;
    }

    // The card takes its final layout space once; only the timeline moves per frame.
    const distance =
      timeline.getBoundingClientRect().top - card.getBoundingClientRect().top;
    const animation = timeline.animate(
      [
        { transform: `translateY(-${distance}px)` },
        { transform: "translateY(0)" },
      ],
      {
        duration: SCORE_CARD_ENTRY_DURATION_MS,
        easing: "cubic-bezier(0.4, 0, 0.2, 1)",
      },
    );
    animation.onfinish = () => setIsInteractive(true);
    return () => {
      animation.onfinish = null;
      animation.cancel();
    };
  }, [timelineRef]);

  return (
    <div
      ref={cardRef}
      inert={!isInteractive}
      className="mb-4 motion-safe:animate-[fade-in_200ms_ease-in-out_100ms_both] sm:mb-6"
    >
      {children}
    </div>
  );
};

interface CustomerDetailContentProps {
  isTimelineLoading: boolean;
  isError: boolean;
  scoreCard: ReactNode;
  timeline: ReactNode;
}

const CustomerDetailContent: FC<CustomerDetailContentProps> = (props) => {
  const { isTimelineLoading, isError, scoreCard, timeline } = props;
  const hasScoreCard = Boolean(scoreCard);
  const [canEnterScores, setCanEnterScores] = useState(false);
  const timelineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasScoreCard || canEnterScores || (isTimelineLoading && !isError)) {
      return;
    }
    const timeout = window.setTimeout(
      () => setCanEnterScores(true),
      prefersReducedMotion() || isError ? 0 : SCORE_CARD_START_DELAY_MS,
    );
    return () => window.clearTimeout(timeout);
  }, [hasScoreCard, canEnterScores, isTimelineLoading, isError]);

  return (
    <div className="flex min-w-0 flex-col">
      {hasScoreCard && canEnterScores && (
        <ScoreCardEntry timelineRef={timelineRef}>{scoreCard}</ScoreCardEntry>
      )}
      <div ref={timelineRef} className="min-w-0">
        {isError ? (
          <p className="text-sm text-muted-foreground">
            Unable to load customer.
          </p>
        ) : isTimelineLoading ? (
          <CustomerTimelineSkeleton />
        ) : (
          timeline
        )}
      </div>
    </div>
  );
};

export const CustomerDetailPage: FC = () => {
  const routeParams = useParams();
  const accountId = asAccountId(requireRouteParam(routeParams, "accountId"));
  const playbookId = asPlaybookId(requireRouteParam(routeParams, "playbookId"));
  const { teammateId } = routeParams;
  const viewedTeammateId = teammateId ? asTeammateId(teammateId) : undefined;
  const [selectedTeammateId, setSelectedTeammateId] =
    useState<TeammateId | null>(null);
  const [selectedPlaybookId, setSelectedPlaybookId] =
    useState<PlaybookId | null>(null);
  const company = useAppSelector(selectCompany);
  const companyTimezone = getCompanyTimezone(company);
  const detailResult = useCustomerDetailQuery(accountId);
  const timelineResult = useCustomerTimelineInfiniteQuery(accountId);
  const detail = detailResult.currentData;
  const pages = timelineResult.currentData?.pages;
  const playbookScores = detail?.playbook_scores;
  const entries = useMemo(
    () => pages?.flatMap((page) => page.entries) ?? [],
    [pages],
  );
  const { loadMoreSentinelRef } = useInfiniteScroll({
    entities: entries,
    onLoadMore:
      timelineResult.hasNextPage &&
      !timelineResult.isFetching &&
      !timelineResult.isError
        ? timelineResult.fetchNextPage
        : undefined,
  });
  const playbookOptions = useMemo<
    readonly SimpleSelectOption<PlaybookId | "all">[]
  >(
    () => [
      { value: "all", label: "All playbooks" },
      ...(detail?.playbooks ?? [])
        .map((playbook) => ({ value: playbook.id, label: playbook.name }))
        .toSorted((a, b) => a.label.localeCompare(b.label)),
    ],
    [detail?.playbooks],
  );
  const teammateOptions = useMemo<
    readonly SimpleSelectOption<TeammateId | "all">[]
  >(() => {
    return [
      { value: "all", label: "All teammates" },
      ...(detail?.teammates ?? [])
        .map((teammate) => ({
          value: teammate.id,
          label: buildTeammateDisplayNames(teammate).primaryDisplayName,
        }))
        .toSorted((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [detail?.teammates]);

  const highlightedRowIds = useMemo(() => {
    if (selectedPlaybookId === null && selectedTeammateId === null) {
      return undefined;
    }
    const matchesFilters = (playbookId: PlaybookId, teammateId: TeammateId) =>
      (selectedPlaybookId === null || playbookId === selectedPlaybookId) &&
      (selectedTeammateId === null || teammateId === selectedTeammateId);
    return new Set([
      ...entries
        .filter((entry) =>
          entry.type === "call"
            ? entry.members.some((member) =>
                matchesFilters(member.playbook_id, member.teammate.id),
              )
            : matchesFilters(entry.playbook_id, entry.teammate.id),
        )
        .map(buildCustomerTimelineRowId),
      ...(detail?.upcoming_actions ?? [])
        .filter((action) =>
          matchesFilters(action.playbook_id, action.teammate.id),
        )
        .map(buildUpcomingActionRowId),
    ]);
  }, [
    entries,
    detail?.upcoming_actions,
    selectedPlaybookId,
    selectedTeammateId,
  ]);

  const modalNavigation = useModalNavigation();

  // Stay under the current route: a member's playbook or teammate route would
  // remount this page.
  const openInteraction: OpenCustomerInteraction = (gradingId, target) =>
    modalNavigation.push({
      pathname: teammateId
        ? appPaths.teammateCustomerCallTranscript.build({
            playbookId,
            teammateId,
            accountId,
            gradingId,
          })
        : appPaths.playbookCustomerCallTranscript.build({
            playbookId,
            accountId,
            gradingId,
          }),
      search: target ? new URLSearchParams(target).toString() : undefined,
    });
  const rows = [
    ...(detail?.upcoming_actions ?? []).map((action) =>
      buildUpcomingActionRow(action, openInteraction, companyTimezone),
    ),
    ...entries.map((entry) =>
      buildCustomerTimelineRow(entry, {
        openInteraction,
        viewedTeammateId,
      }),
    ),
  ];

  const isError =
    (detailResult.isError && !detail) || (timelineResult.isError && !pages);

  return (
    <PageLayout>
      <PageHeader
        title={
          <span className={PH_MASK_CLASS}>{detail?.name ?? "Customer"}</span>
        }
        isLoading={detailResult.isLoading}
        actions={
          detail && (detail.phone_number || detail.contact_email) ? (
            <CustomerInfoPopover
              phoneNumber={detail.phone_number}
              contactEmail={detail.contact_email}
            />
          ) : undefined
        }
        filterBar={
          <FilterBar
            isLoading={
              (timelineResult.isFetching &&
                !timelineResult.isFetchingNextPage) ||
              detailResult.isFetching
            }
            leadingItems={
              <SimpleSelect
                label="Playbook"
                isLabelHidden
                isDisabled={!detail}
                options={playbookOptions}
                value={selectedPlaybookId ?? "all"}
                onChange={(value) =>
                  setSelectedPlaybookId(value === "all" ? null : value)
                }
                size={Size.SMALL}
              />
            }
            items={
              <div className={twMerge("contents", PH_MASK_CLASS)}>
                <SimpleSelect
                  label="Teammate"
                  isLabelHidden
                  isDisabled={!detail}
                  options={teammateOptions}
                  value={selectedTeammateId ?? "all"}
                  onChange={(value) =>
                    setSelectedTeammateId(value === "all" ? null : value)
                  }
                  size={Size.SMALL}
                />
              </div>
            }
            onReset={
              selectedTeammateId !== null || selectedPlaybookId !== null
                ? () => {
                    setSelectedTeammateId(null);
                    setSelectedPlaybookId(null);
                  }
                : undefined
            }
          />
        }
      />
      <PageBody>
        <CustomerDetailContent
          key={accountId}
          isTimelineLoading={!pages}
          isError={isError}
          scoreCard={
            // Split rows carry no scores, so a card of only split rows has
            // nothing to show.
            playbookScores?.some((score) => !score.is_split) ? (
              <CustomerPlaybookScores
                scores={playbookScores}
                selectedPlaybookId={selectedPlaybookId}
                selectedTeammateId={selectedTeammateId}
                onSelect={(playbookId, teammateId) => {
                  setSelectedPlaybookId(playbookId);
                  setSelectedTeammateId(teammateId);
                }}
              />
            ) : null
          }
          timeline={
            <div className="[--customer-timeline-sticky-top:6.5rem] sm:[--customer-timeline-sticky-top:7.5rem]">
              <CustomerTimeline
                rows={rows}
                highlightedRowIds={highlightedRowIds}
                isLoadingMore={timelineResult.isFetchingNextPage}
              />
              {timelineResult.isError ? (
                <Button
                  variant={ButtonVariant.Secondary}
                  size={Size.SMALL}
                  className="mt-5"
                  onClick={() => void timelineResult.fetchNextPage()}
                >
                  Retry loading activity
                </Button>
              ) : timelineResult.hasNextPage ? (
                <div
                  ref={loadMoreSentinelRef}
                  className="mt-5 min-h-8"
                  aria-hidden="true"
                />
              ) : null}
            </div>
          }
        />
      </PageBody>
      <NavigationSlideoverContainer />
    </PageLayout>
  );
};
